import { inject, Injectable } from '@angular/core';
import { Category, Classification } from '@app/core/model/other/classification';
import ApiService from '@services/api.service';
import { AppManager } from '@services/managers/app.manager';
import { gql } from 'apollo-angular';
import { plainToInstance } from 'class-transformer';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable()
export class ClassificationService {
  private readonly appManager = inject(AppManager);
  private readonly apiService = inject(ApiService);

  public categories: Category[];

  /**
   * Make an API request to check if a Classification is linked to the current Organization
   * that the current user have permission to view.
   * @return Observable of a boolean, true if a classification exists, otherwise false.
   */
  public classificationExistsInOrganization(code: string): Observable<boolean> {
    const query = gql`
      query ClassificationExists($organizationId: String!, $code: String!) {
        classificationExists(organizationId: $organizationId, code: $code)
      }
    `;
    const variables = {
      organizationId: this.appManager.currentOrganization.id,
      code: code
    };
    return this.apiService.query({query, variables})
      .pipe(map(data => data['classificationExists'] as boolean));
  }

  /**
   * Make an API request to fetch the current Organization's technical classifications.
   * @param code Code to retrieve classification
   * @param level determines the path level of technical classifications to fetch,
   * if not specified, all the technical classifications will be fetched.
   * @return Observable of the current Organization's technical classifications.
   */
  public getClassificationByCode(code: string, level?: number): Observable<Category[]> {
    if (this.categories?.length > 0) {
      return of(this.categories);
    } else {
      const query = gql`
        query Classification($organizationId: String!, $includePathList: Boolean!, $code: String!, $level: Int) {
          classification(organizationId: $organizationId, code: $code, level: $level) {
            id
            code
            organizationId
            categoriesList {
              label
              code
              pathList @include(if: $includePathList)
            }
          }
        }
      `;
      const variables = {
        organizationId: this.appManager.currentOrganization.id,
        includePathList: !level || level > 1,
        code,
        level
      };
      return this.apiService.query({query, variables})
        .pipe(
          map(data => {
            const classification = plainToInstance(Classification, data['classification']);
            this.categories = classification.categories;
            return this.categories;
          })
        );
    }
  }
}
